import { useMemo, useState } from "react";
import { diffLines } from "@/lib/auditDiff";

/** Lines shown before the value collapses behind a "show more" control. */
const COLLAPSED_LINE_COUNT = 15;

interface AuditDiffValueProps {
  before: string;
  after: string;
  side: "before" | "after";
}

/**
 * Renders one side of a long text value — a report section body — as lines, with only the
 * lines that differ from the other side highlighted. The field-level diff alone would put
 * two multi-KB walls of markdown side by side and leave the reader to spot the change.
 */
export const AuditDiffValue = ({ before, after, side }: AuditDiffValueProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Diff lines have no natural identity, so their position is their identity. Baked in
  // here rather than passed as an array-index key at the call site.
  const lines = useMemo(
    () => diffLines(before, after)[side].map((line, index) => ({ ...line, id: `${side}-${index}` })),
    [before, after, side]
  );

  const isCollapsible = lines.length > COLLAPSED_LINE_COUNT;
  const visibleLines = isExpanded ? lines : lines.slice(0, COLLAPSED_LINE_COUNT);
  const hiddenCount = lines.length - visibleLines.length;

  if (lines.length === 0) {
    return <div className="text-sm text-muted-foreground">—</div>;
  }

  return (
    <div className="text-sm">
      <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words">
        {visibleLines.map((line) => (
          <div key={line.id} className={line.changed ? "bg-yellow-100/60" : undefined}>
            {line.text || " "}
          </div>
        ))}
      </div>
      {isCollapsible && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="mt-1 text-xs font-medium text-muted-foreground hover:text-foreground underline"
        >
          {isExpanded ? "Show less" : `Show ${hiddenCount} more lines`}
        </button>
      )}
    </div>
  );
};
